import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMessages } from "@/locales/messages";
import { toast } from "@/components/ui/toast-variants";
import { useToast } from "./use-toast";
import { registerUndo, cancelUndo, clearAllUndos } from "@/services/undo/undoManager";

/**
 * Hook to show an undo toast with a localized cancel button.
 * The toast registers an undo action and clears all pending actions
 * when the route changes.
 */
export function useUndoToast() {
  const { dismiss } = useToast();
  const { undo } = useMessages();
  const location = useLocation();

  // Clear pending undo actions on every route change
  useEffect(() => {
    clearAllUndos();
    dismiss();
  }, [location, dismiss]);

  return useCallback(
    (actionId: string, message: string, restoreFn: () => void, timeoutMs = 5000) => {
      registerUndo(actionId, restoreFn, timeoutMs);
      let toastId: string | number;
      toastId = toast({
        description: message,
        duration: timeoutMs,
        action: {
          label: undo.cancel,
          onClick: () => {
            cancelUndo(actionId);
            dismiss(toastId);
          },
        },
      });
    },
    [dismiss, undo]
  );
}
